import {applyAuxEvents} from './auxEvents.js';
import {stats} from 'stats/stats.js';
/**
 * Triggers the given tracks, applying any choking/matching that
 * may be required
 */
export function triggerTracks(
    sequencerEngine,
    tracks,
    trackToTriggers,
    time,
    currentStep)
{
    // first update the choke datastructure based on the events
    tracks
        .filter(t => t !== undefined)
        .forEach(t => t.chokes.onStep());

    resolveGrabs(trackToTriggers, tracks);
    trackToTriggers = resolveScriptedChokes(trackToTriggers, tracks);

    for (let i in trackToTriggers) {
        if (tracks[i] === undefined || tracks[i].chokes === undefined ||
            tracks[i].chokes.choke === undefined) {
            continue;
        }
        let trackToChoke = tracks[i].chokes.choke;

        let triggers = trackToTriggers[i];
        for (let j in triggers) {
            let trigger = triggers[j];
            let duration = trigger.stepData.duration * 2;
            trackToChoke.chokes.newChoke(duration, trigger.time);
        }
    }

    trackToTriggers = handleMidi(trackToTriggers, tracks, time, currentStep);

    let _tracks = [];
    let max = Math.max(... tracks.map(t => t.trackNumber));
    for (let i=0; i <= max; i++) {
        let track = tracks.find(t => t.trackNumber === i);
        _tracks.push(track);
    }
    tracks = _tracks;
    
    for (let sub of sequencerEngine.subSequences) {
        for (let track of sub.tracks) {
            if (!track) {
                continue;
            }
            // i want to be able to do this by track though
            let transposes = track.notesTriggered || {};
            let keys = Object.keys(transposes);
            for (let t of keys) {
                transposes[t] -= 0.5;
                if (transposes[t] <= 0) {
                    delete transposes[t];
                }
            }
            track.notesTriggered = {... transposes};
        }
    }

    for (let i in trackToTriggers) {
        let track = tracks[i];
        if (!track) {
        }

        // keep track of which notes are playing
        // i want to be able to do this by track though
        let transposes = (track && track.notesTriggered) || {};

        if (track === undefined ||
            track.chokes === undefined ||
            track.chokes.isChoked()) {
            if (track) {
                track.triggerCount = 0;
            }
            continue;
        }

        if (trackToTriggers[i] === undefined) {
            track.triggerCount = 0;
            continue;
        }
        if (trackToTriggers[i].length === 0) {
            if (currentStep % 2 === 0) {
                track.triggerCount = trackToTriggers[i].length;
            }
        } else {
            track.triggerCount = trackToTriggers[i].length;
        }
        trackToTriggers[i].forEach(
            trigger => {
                let transpose = Math.round(trigger.stepData.transpose);
                let dur = transposes[transpose] || 0;
                let offset = track.isWarped() ? 4 : 1;
                transposes[transpose] = Math.max(
                    dur,
                    Math.ceil(trigger.stepData.duration + offset));
                let stepData = applyAuxEvents(trigger.stepData, track);
                track.trigger(stepData, trigger.time, currentStep);
            });

        track.notesTriggered = {... transposes};
    }
    stats(sequencerEngine);
}

const resolveScriptedChokes = (trackToTriggers) => {
    let tracksToChoke = new Set([]);
    for (let i in trackToTriggers) {
        let chokeTriggers = trackToTriggers[i]
            .filter(x => x.chokes && x.chokes.length > 0);
        chokeTriggers.forEach(
            trigger =>
                trigger.chokes.forEach(
                    x => tracksToChoke.add(x.trackNumber)));
    }

    let newTrackToTriggers = {};
    for (let i in trackToTriggers) {
        if (!tracksToChoke.has(parseInt(i))) {
            newTrackToTriggers[i] = trackToTriggers[i];
        }
    }

    return newTrackToTriggers;
};

const resolveGrabs = (trackToTriggers, tracks) => {
    for (let i in trackToTriggers) {
        let grabTriggers = trackToTriggers[i]
            .filter(x => x.grabs && x.grabs.length > 0);

        let counter = 0;
        let max = 100;

        while (grabTriggers.length > 0) {
            if (++ counter >= max) {
                break;
            }

            // for each lazy trigger, find out the param from the track
            let grabTrigger = grabTriggers.shift();
            for (let grab of grabTrigger.grabs) {
                if (hasGrabsToResolve(grab, trackToTriggers)) {
                    grabTriggers.push(grabTrigger);
                    break;
                }
                resolveGrab(grabTrigger, grab, trackToTriggers, tracks);
                grabTrigger.resolved = true;
            }
        }
    }
};

const hasGrabsToResolve = (grab, trackToTriggers) => {
    let {trackNumber, fromParam, toParam} = grab;
    let triggersToGrab = trackToTriggers[trackNumber];
    if (triggersToGrab === undefined ||
        triggersToGrab.length === 0) {
        return false;
    }

    let trigger = triggersToGrab[0];
    return trigger.grabs.length > 0 && !trigger.resolved;
};

const resolveGrab = (grabTrigger, grab, trackToTriggers, tracks) => {
    let {trackNumber, add, fromParam, toParam, isTime} = grab;
    let triggersToGrab = trackToTriggers[trackNumber];
    let track = tracks[trackNumber];
    
    let value = undefined;
    let toParamObj = grabTrigger.stepData[toParam + 'Parameter'];
    let fromMin = -32;
    let fromMax = 32;

    if (triggersToGrab === undefined ||
        triggersToGrab.length === 0) {

        // if fromParam === transpose then try to use notesTriggered
        if (fromParam === "transpose") {
            if (track.notesTriggered) {
                value = getBiggestDuration(track.notesTriggered);
                let dur = track.notesTriggered[value];
                if (value === undefined || dur < 2) {
                    return false;
                }
                value = parseFloat(value);
            }
        } else {
            return false;
        }
    } else {
        // get first trigger
        let trigger = triggersToGrab[0];
        if (grab.isTime) {
            grabTrigger.time = trigger.time;
            return true;
        }
        let fromParamObj = trigger.stepData[fromParam + 'Parameter'];
        if (fromParamObj === undefined ||
            toParamObj === undefined) {
            return false;
        }
        value = fromParamObj.get();
        fromMin = fromParamObj.min;
        fromMax = fromParamObj.max;
    }
    let normalizedValue = (value - fromMin) / (fromMax - fromMin);
    let v = toParamObj.min + normalizedValue * (toParamObj.max - toParamObj.min);
    toParamObj.set(add ? toParamObj.get() + v : v);
};


const handleMidi = (trackToTriggers, tracks, time, stepNumber) => {
    if (tracks.length === 0) {
        return trackToTriggers;
    }
    let subSequence = tracks[0].subSequence;
    subSequence.midiEffects.onNewStep();

    let _trackToTriggers = {};
    for (let i in trackToTriggers) {
        let triggers = trackToTriggers[i];
        let track = tracks[i];
        if ((track && track.isFrozen && track.skipMidiMaster) || (track && track.skipMidiMaster)) {
            if (!_trackToTriggers[track.trackNumber]) {
                _trackToTriggers[track.trackNumber] = [];
            }
            _trackToTriggers[track.trackNumber] = triggers;
            continue;
        }
        triggers.forEach(
            t => 
                subSequence.midiEffects.input(t));
    }
    let triggers = subSequence.midiEffects.output(time, stepNumber, stepNumber);
    for (let trigger of triggers) {
        if (trigger.stepData && trigger.stepData.track) {
            let trackNumber = trigger.stepData.track.trackNumber;
            if (trackNumber === undefined ) { continue; }
            if (!_trackToTriggers[trackNumber]) {
                _trackToTriggers[trackNumber] = [];
            }
            _trackToTriggers[trackNumber].push(trigger);
        }
        
    }
    return _trackToTriggers;
};

const getBiggestDuration = (notesTriggered) => {
    let max = -4;
    let key = undefined;
    for (let note in notesTriggered) {
        if (max < notesTriggered[note]) {
            key = note;
            max = notesTriggered[note];
        }
    }
    return key;
}
